import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  FolderKanban,
  Code2,
  Package,
  Rocket,
  User,
  Activity,
  DollarSign,
  CreditCard,
  Settings,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { useWalletStore } from '../../stores/useWalletStore';
import { HomePage } from '../pages/HomePage';
import { ProjectsPage } from '../pages/ProjectsPage';
import { ProjectDetailPage } from '../pages/ProjectDetailPage';
import { IDEWorkspace } from '../pages/IDEWorkspace';
import { PrefabsPage } from '../pages/PrefabsPage';
import { PrefabDetailPage } from '../pages/PrefabDetailPage';
import { PrefabDeployPage } from '../pages/PrefabDeployPage';
import { DeploymentsPage } from '../pages/DeploymentsPage';
import { AccountPage } from '../pages/AccountPage';
import { ActivityPage } from '../pages/ActivityPage';
import { EarningsPage } from '../pages/EarningsPage';
import { SubscriptionsPage } from '../pages/SubscriptionsPage';
import { SettingsPage } from '../pages/SettingsPage';
import styles from './AppShell.module.css';

type NavSection = { label: string; items: NavItem[] };

interface NavItem {
  path: string;
  icon: React.ReactNode;
  label: string;
  match?: RegExp;
  badge?: number;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Platform',
    items: [
      { path: '/', icon: <Home size={18} />, label: 'Home', match: /^\/$/ },
      { path: '/projects', icon: <FolderKanban size={18} />, label: 'Projects', match: /^\/projects/ },
      { path: '/ide', icon: <Code2 size={18} />, label: 'IDE', match: /^\/ide/ },
      { path: '/prefabs', icon: <Package size={18} />, label: 'Prefabs', match: /^\/prefabs/ },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/deployments', icon: <Rocket size={18} />, label: 'Deployments', match: /^\/deployments/ },
      { path: '/activity', icon: <Activity size={18} />, label: 'Activity', match: /^\/activity/ },
    ],
  },
  {
    label: 'Business',
    items: [
      { path: '/earnings', icon: <DollarSign size={18} />, label: 'Earnings', match: /^\/earnings/ },
      { path: '/subscriptions', icon: <CreditCard size={18} />, label: 'Subscriptions', match: /^\/subscriptions/ },
    ],
  },
  {
    label: 'Settings',
    items: [
      { path: '/account', icon: <User size={18} />, label: 'Account', match: /^\/account/ },
      { path: '/settings', icon: <Settings size={18} />, label: 'Settings', match: /^\/settings/ },
    ],
  },
];

function getBreadcrumb(pathname: string): { section: string; page: string } | null {
  if (pathname === '/') return null;
  const segments = pathname.split('/').filter(Boolean);
  const label = segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  if (segments.length === 1) return { section: label, page: '' };
  return { section: label, page: segments.slice(1).join(' / ') };
}

function walletDotColor(status: string): string {
  switch (status) {
    case 'connected': return '#22c55e';
    case 'connecting': return '#f59e0b';
    case 'locked': return '#ef4444';
    default: return '#6b7280';
  }
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore(s => s.logout);
  const address = useAuthStore(s => s.address);
  const loadProjects = useProjectStore(s => s.loadProjects);
  const walletStatus = useWalletStore(s => s.connectionStatus);
  const network = useWalletStore(s => s.network);

  useEffect(() => {
    loadProjects();
  }, [loadProjects, address]);

  useEffect(() => {
    if (address && walletStatus === 'disconnected') {
      const ws = useWalletStore.getState();
      ws.detectProvider().then(p => {
        // Only silently reconnect if the provider says it's already connected
        // (user previously approved). Never call connect() unprompted — that
        // opens the extension popup.
        if (p?.isNatos && typeof p.isConnected === 'function' && p.isConnected()) {
          ws.connect();
        }
      });
    }
  }, [address, walletStatus]);

  const isIDEMode = location.pathname.startsWith('/ide');
  const breadcrumb = getBreadcrumb(location.pathname);
  const shortAddr = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

  return (
    <div className={styles.shell}>
      {/* ─── Top header bar ─── */}
      <header className={styles.globalHeader}>
        <div className={styles.headerLeft}>
          <img src="/logo.png" alt="Rivellum" className={styles.headerLogo} />
          <span className={styles.headerBrand}>Rivellum</span>
        </div>
        <div className={styles.headerRight}>
          {address && (
            <div className={styles.headerWallet}>
              <span
                className={styles.walletDot}
                style={{ background: walletDotColor(walletStatus) }}
              />
              <span className={styles.headerWalletAddr}>{shortAddr}</span>
              {network && (
                <span className={styles.headerWalletNet}>
                  {network.network ?? network.id ?? 'unknown'}
                </span>
              )}
            </div>
          )}
          <button className={styles.headerLogout} onClick={logout} title="Sign out">
            <LogOut size={14} />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      <div className={styles.shellBody}>
        {/* ─── Sidebar ─── */}
        <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
          <nav className={styles.sidebarNav}>
            {NAV_SECTIONS.map(section => (
              <div key={section.label} className={styles.navSection}>
                <div className={styles.navSectionLabel}>{section.label}</div>
                {section.items.map(item => {
                  const active = item.match
                    ? item.match.test(location.pathname)
                    : location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      className={`${styles.navItem} ${active ? styles.active : ''}`}
                      onClick={() => navigate(item.path)}
                      title={collapsed ? item.label : undefined}
                      aria-label={item.label}
                    >
                      <span className={styles.navItemIcon}>{item.icon}</span>
                      <span className={styles.navItemLabel}>{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className={styles.navItemBadge}>{item.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className={styles.sidebarFooter}>
            <button
              className={styles.collapseButton}
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
          </div>
        </aside>

        {/* ─── Main area ─── */}
        <div className={styles.main}>
          {!isIDEMode && breadcrumb && (
            <div className={styles.topBar}>
              <div className={styles.breadcrumb}>
                <span>{breadcrumb.section}</span>
                {breadcrumb.page && (
                  <>
                    <span className={styles.breadcrumbSep}>/</span>
                    <span className={styles.breadcrumbCurrent}>{breadcrumb.page}</span>
                  </>
                )}
              </div>
            </div>
          )}

        <div className={styles.content}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id/*" element={<ProjectDetailPage />} />
            <Route path="/ide" element={<IDEWorkspace />} />
            <Route path="/ide/:projectId" element={<IDEWorkspace />} />
            <Route path="/prefabs" element={<PrefabsPage />} />
            <Route path="/prefabs/:prefabId" element={<PrefabDetailPage />} />
            <Route path="/prefabs/:prefabId/deploy" element={<PrefabDeployPage />} />
            <Route path="/deployments" element={<DeploymentsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/earnings" element={<EarningsPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
      </div>
    </div>
  );
}
